import { FetchError } from '@commenda/utils';
import {
  parseJsonEventStream,
  uiMessageChunkSchema,
  type ChatTransport,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';

import {
  readAgentConversation,
  sendAgentConversationMessage,
  streamAgentConversationMessage,
} from './agentConversationsApi';
import {
  COMMENDA_CHAT_PROFILE_KEY,
  DEFAULT_POLL_INTERVAL_MS,
  REGENERATE_PLACEHOLDER_MESSAGE,
  SEND_FAILED_MESSAGE,
} from './constants';
import { QueuedConversationTransportHelper } from './queued-conversation-transport-helper';
import { ReplyChunkEmitter } from './reply-chunk-emitter';
import type {
  AgentConversationEnvelope,
  AgentConversationEnvelopeMessage,
  AgentReplyBaseline,
  QueuedConversationTransportOptions,
  SendAgentConversationMessageBody,
} from './types';

/** The turn never reached the server: the read before the send or the send itself failed. A failure
 * after the send landed is a poll's, and the turn is stored by then. */
export class AgentTurnNotSentError extends FetchError {}

/** The server still stores the reply, so the chat recovers it from the read. */
export class AgentLiveReplyLostError extends Error {}

const REPLY_ENDING_CHUNK_TYPES: ReadonlySet<UIMessageChunk['type']> = new Set([
  'finish',
  'abort',
  'error',
]);

/**
 * The shared chat UI against a queued profile.
 *
 * Chat streams its reply: the send is answered with the reply's chunks as they are written. The builder
 * and research do not: a send is accepted and a worker picks it up, so there is no response body to
 * read. For them this transport posts the turn, then polls the conversation read and emits the
 * assistant message's parts as they appear, which is what lets one renderer serve a streaming surface
 * and a queued one without either knowing which it is.
 */
export class QueuedConversationTransport implements ChatTransport<UIMessage> {
  /** The AI SDK hands a resumed stream no abort signal (it makes one only after the stream is returned),
   * so Stop could not end a resumed poll. The transport keeps its own and the chat's stop fires it. */
  private resumeAbortController: AbortController | null = null;

  constructor(
    private readonly conversationId: string,
    private readonly options: QueuedConversationTransportOptions = {}
  ) {}

  async sendMessages({
    messages,
    abortSignal,
    body,
    trigger,
  }: Parameters<ChatTransport<UIMessage>['sendMessages']>[0]): Promise<
    ReadableStream<UIMessageChunk>
  > {
    const { text, attachments } =
      QueuedConversationTransportHelper.getTurnContent(
        messages[messages.length - 1]
      );

    // Read before sending: a queued reply row is written after the send returns, so the rows already
    // here are the only way to tell the new reply from the previous one.
    const before = await this.readConversation(abortSignal);
    const shared = {
      ...this.options.buildSendBody?.(),
      ...(body as Partial<SendAgentConversationMessageBody> | undefined),
    };
    const isRegenerate =
      trigger === 'regenerate-message' &&
      QueuedConversationTransportHelper.canRegenerate(before, text);
    const sendBody: SendAgentConversationMessageBody = isRegenerate
      ? {
          ...shared,
          answerTo: undefined,
          message: text.trim() ? text : REGENERATE_PLACEHOLDER_MESSAGE,
          regenerate: true,
        }
      : {
          ...shared,
          message: text,
          ...(attachments.length > 0 ? { attachments } : {}),
        };

    if (before.conversation.profile === COMMENDA_CHAT_PROFILE_KEY)
      return this.streamLiveReply(sendBody, abortSignal);

    const sent = await sendAgentConversationMessage(
      this.conversationId,
      sendBody,
      abortSignal
    );

    if (!sent.success) {
      // Rethrown as the SDK's AbortError so a Stop during the send is not shown as a failure.
      abortSignal?.throwIfAborted();
      if (sent.status === 401) this.options.onUnauthorized?.();
      throw new AgentTurnNotSentError(
        sent.error ?? SEND_FAILED_MESSAGE,
        {},
        sent.status ?? 0
      );
    }

    this.options.onTurnSent?.();

    return this.pollForReply(
      abortSignal,
      {
        priorMessageIds: new Set(
          before.messages.map((message) => message.messageId)
        ),
        drawn: null,
      },
      QueuedConversationTransportHelper.pollBudget(before)
    );
  }

  private async streamLiveReply(
    sendBody: SendAgentConversationMessageBody,
    abortSignal: AbortSignal | undefined
  ): Promise<ReadableStream<UIMessageChunk>> {
    const sent = await streamAgentConversationMessage(
      this.conversationId,
      sendBody,
      abortSignal
    );

    if (!sent.success || !sent.data) {
      abortSignal?.throwIfAborted();
      if (sent.status === 401) this.options.onUnauthorized?.();
      throw new AgentTurnNotSentError(
        sent.error ?? SEND_FAILED_MESSAGE,
        {},
        sent.status ?? 0
      );
    }

    this.options.onTurnSent?.();

    return QueuedConversationTransport.followLiveReply(sent.data, abortSignal);
  }

  private static followLiveReply(
    body: ReadableStream<Uint8Array>,
    abortSignal: AbortSignal | undefined
  ): ReadableStream<UIMessageChunk> {
    const reader = parseJsonEventStream({
      stream: body,
      schema: uiMessageChunkSchema,
    }).getReader();
    let ended = false;

    return new ReadableStream<UIMessageChunk>({
      async pull(controller) {
        try {
          const { done, value } = await reader.read();
          if (done) {
            if (ended) controller.close();
            else controller.error(new AgentLiveReplyLostError());
            return;
          }
          if (!value.success) throw new AgentLiveReplyLostError();

          if (REPLY_ENDING_CHUNK_TYPES.has(value.value.type)) ended = true;
          controller.enqueue(value.value);
        } catch (error) {
          controller.error(
            abortSignal?.aborted ? error : new AgentLiveReplyLostError()
          );
        }
      },
      cancel(reason) {
        return reader.cancel(reason);
      },
    });
  }

  /** Picks up a reply after a reload, a start-screen send or a card answer that resumes a paused run;
   * without it the transcript freezes until the next send. */
  async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
    const envelope = await readAgentConversation(this.conversationId).catch(
      () => null
    );
    const baseline = envelope
      ? QueuedConversationTransportHelper.resumeBaseline(envelope, Date.now())
      : null;
    if (!baseline) return null;

    this.resumeAbortController = new AbortController();

    return this.pollForReply(
      this.resumeAbortController.signal,
      baseline,
      QueuedConversationTransportHelper.pollBudget(envelope)
    );
  }

  /** Ends a resumed poll. A poll started by a send already ends through the SDK's own signal. */
  stopResumedReply(): void {
    this.resumeAbortController?.abort();
    this.resumeAbortController = null;
  }

  private async readConversation(
    abortSignal: AbortSignal | undefined
  ): Promise<AgentConversationEnvelope> {
    try {
      return await readAgentConversation(this.conversationId, abortSignal);
    } catch (error) {
      abortSignal?.throwIfAborted();
      const status = error instanceof FetchError ? error.status : 0;
      if (status === 401) this.options.onUnauthorized?.();
      throw new AgentTurnNotSentError(SEND_FAILED_MESSAGE, {}, status);
    }
  }

  private pollForReply(
    abortSignal: AbortSignal | undefined,
    baseline: AgentReplyBaseline,
    pollBudget: number
  ): ReadableStream<UIMessageChunk> {
    const { conversationId } = this;
    const interval = this.options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const { onUnauthorized } = this.options;
    let replyId = baseline.drawn?.messageId ?? null;

    /** Locked to the first reply found, so a later row can never swap in mid-turn. */
    const acceptReply = (
      envelope: AgentConversationEnvelope
    ): AgentConversationEnvelopeMessage | null => {
      if (replyId !== null)
        return (
          envelope.messages.find((message) => message.messageId === replyId) ??
          null
        );

      const reply = QueuedConversationTransportHelper.newestAssistantMessage(
        envelope,
        baseline.priorMessageIds
      );
      if (reply) replyId = reply.messageId;
      return reply;
    };

    return new ReadableStream<UIMessageChunk>({
      start(controller) {
        const emitter = new ReplyChunkEmitter(controller, baseline.drawn);

        // Recursion rather than a loop: each tick waits for the previous read to land, and the
        // house lint rules refuse an await inside a loop.
        const tick = async (attemptsLeft: number): Promise<void> => {
          if (attemptsLeft <= 0) {
            emitter.close([
              { type: 'error', errorText: 'The agent did not finish in time.' },
            ]);
            return;
          }

          if (abortSignal?.aborted) {
            emitter.close([{ type: 'abort' }]);
            return;
          }

          await new Promise((resolve) => {
            setTimeout(resolve, interval);
          });

          let envelope: AgentConversationEnvelope;
          try {
            envelope = await readAgentConversation(conversationId, abortSignal);
          } catch (error) {
            if (abortSignal?.aborted) {
              emitter.close([{ type: 'abort' }]);
              return;
            }
            if (error instanceof FetchError && error.status === 401)
              onUnauthorized?.();
            throw error;
          }

          // Until this turn's reply row exists, a finished state is the previous turn's, not this one's.
          const reply = acceptReply(envelope);
          if (reply) {
            emitter.emitReply(reply);

            // A reply parked at a gate settles this turn too: the next move is the person's, so
            // holding the stream open would leave the composer disabled.
            if (QueuedConversationTransportHelper.isReplySettled(reply)) {
              emitter.close([{ type: 'finish' }]);
              return;
            }
          }

          await tick(attemptsLeft - 1);
        };

        tick(pollBudget).catch((error) => controller.error(error));
      },
    });
  }
}
